import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const Terms: React.FC = () => {
  useEffect(() => {
    document.title = 'Terms of Service — Pixano';
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />
      <main className="flex-1">
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <p className="text-sm uppercase tracking-wide text-blue-600 font-semibold">
                Legal
              </p>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
              <p className="text-gray-600 mb-2">
                Welcome to Pixano. These Terms of Service (&quot;Terms&quot;) govern your access to and use
                of the Pixano website, mobile application, portals, and related services
                (collectively, the &quot;Services&quot;). These Terms form a legally binding agreement
                between you and Pixano AI LLC (&quot;Pixano&quot;, &quot;we&quot;, &quot;us&quot;).
              </p>
              <p className="text-sm text-gray-500">
                Last updated: January 14, 2026
              </p>
            </div>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Introduction and Acceptance of Terms</h2>
                <p className="mb-3">
                  Pixano provides a platform where users can submit photos, videos, and other content to help create training datasets
                  for machine learning and artificial intelligence systems. If you do not agree to these Terms, you may not use the Services.
                </p>
                <p>
                  Your use of the Services is also governed by our Privacy Policy, which is incorporated into these Terms by reference.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. Eligibility</h2>
                <p className="mb-3">
                  You must be at least 13 years old (or the age of digital consent in your jurisdiction, whichever is higher) to use the Services.
                  If you are under 18, you represent that you have your parent&apos;s or guardian&apos;s permission to use the Services.
                </p>
                <p>
                  The Services are not intended for children under 13. We do not knowingly collect personal information from children under 13.
                  If we become aware that we have collected information from a child under 13, we will take steps to delete such information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. Accounts and Security</h2>
                <p className="mb-3">
                  To use certain features of the Services, you may need to create an account. You agree to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate, current, and complete information when creating your account</li>
                  <li>Maintain and update your account information to keep it accurate</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Accept responsibility for all activities that occur under your account</li>
                  <li>Notify Pixano immediately of any unauthorized use of your account</li>
                </ul>
                <p className="mt-3">
                  You may not share your account credentials with others or create multiple accounts to circumvent our policies.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. User Content and License Grant</h2>
                <p className="mb-3">
                  <strong>Ownership:</strong> You retain ownership of any photos, videos, metadata, annotations, and other content you submit
                  through the Services (&quot;User Content&quot;). However, by submitting User Content, you grant Pixano and its affiliates,
                  service providers, and business partners a worldwide, royalty-free, non-exclusive, perpetual, irrevocable, sublicensable,
                  and transferable license to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>Host, store, process, analyze, and display User Content</li>
                  <li>Create, modify, and use derivative works based on User Content, including annotations, labels, metadata, and other
                      data structures derived from or associated with User Content</li>
                  <li>Use User Content and derivative works to operate, maintain, and improve the Services</li>
                  <li>Use User Content and derivative works to train, develop, and improve machine learning models, artificial intelligence
                      systems, and related technologies</li>
                  <li>Create datasets, collections, and other compilations incorporating User Content and derivative works</li>
                  <li>License, sell, distribute, and otherwise make available datasets and compilations to business customers, including
                      for use in training machine learning models and developing AI systems</li>
                  <li>Grant sublicenses to third parties (including business customers and service providers) for the above purposes</li>
                </ul>
                <p className="mb-3">
                  This license applies to User Content that has been approved through our moderation process. You represent and warrant that:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>You own or have the necessary rights to grant the license described above</li>
                  <li>Your User Content does not infringe the rights of any third party</li>
                  <li>Your User Content complies with these Terms and applicable laws</li>
                </ul>
                <p>
                  <strong>Important:</strong> Once User Content is incorporated into datasets that have been licensed or distributed to
                  business customers, Pixano may not be able to remove that content from those datasets. However, we will make reasonable
                  efforts to cease using your User Content in new datasets and services upon your request, where feasible.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. Acceptable Use and Prohibited Content</h2>
                <p className="mb-3">
                  You agree to use the Services only for lawful purposes and in accordance with these Terms. You agree not to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>Submit content that is illegal, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable</li>
                  <li>Submit content that contains personal information of others without their consent, or sensitive personal information
                      (such as financial information, health records, or government-issued identification)</li>
                  <li>Submit content that infringes intellectual property rights, privacy rights, or other rights of third parties</li>
                  <li>Submit content that contains malware, viruses, or other harmful code</li>
                  <li>Attempt to reverse engineer, decompile, or disassemble the Services</li>
                  <li>Use automated systems (bots, scrapers, etc.) to access the Services without authorization</li>
                  <li>Interfere with or disrupt the operation of the Services</li>
                  <li>Impersonate others or provide false information</li>
                  <li>Engage in fraud, abuse, or any activity that violates applicable laws</li>
                </ul>
                <p>
                  Pixano reserves the right to determine, in its sole discretion, what constitutes prohibited content or activity.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Moderation, Reporting, Enforcement, and Termination</h2>
                <p className="mb-3">
                  <strong>Moderation:</strong> Pixano may review, approve, reject, or remove User Content at any time, with or without notice.
                  We are not obligated to review all User Content, but we reserve the right to do so. User Content that violates these Terms
                  or is otherwise objectionable may be removed.
                </p>
                <p className="mb-3">
                  <strong>Reporting:</strong> You may report content or behavior that you believe violates these Terms. We will review reports
                  and take action as we deem appropriate.
                </p>
                <p className="mb-3">
                  <strong>Enforcement:</strong> Pixano may take any action we deem appropriate in response to violations of these Terms, including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>Warning users</li>
                  <li>Removing User Content</li>
                  <li>Suspending or terminating accounts</li>
                  <li>Withholding or forfeiting rewards</li>
                  <li>Taking legal action where appropriate</li>
                </ul>
                <p className="mb-3">
                  <strong>Termination:</strong> You may stop using the Services at any time. Pixano may suspend or terminate your access
                  to the Services, with or without cause or notice, if you violate these Terms or engage in conduct that we determine is
                  harmful to Pixano, other users, or third parties. Repeat offenders may be permanently banned.
                </p>
                <p>
                  Upon termination, your right to use the Services will immediately cease. However, the license grants described in Section 4
                  will survive termination to the extent necessary for Pixano to continue using User Content that has already been incorporated
                  into datasets or services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. Rewards Program Terms</h2>
                <p className="mb-3">
                  Pixano may offer optional in-app rewards, points, tokens, or other incentives (&quot;Rewards&quot;) to users who submit approved
                  User Content. The Rewards program is subject to the following terms:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>Rewards are discretionary and may be modified, suspended, or terminated at any time</li>
                  <li>Rewards have no cash value and are not redeemable for money</li>
                  <li>Rewards are not guaranteed and may vary based on factors such as content quality, task type, and availability</li>
                  <li>Pixano may withhold, forfeit, or revoke Rewards if we determine, in our sole discretion, that you have engaged in fraud,
                      abuse, or violation of these Terms</li>
                  <li>Rewards may be subject to expiration or other limitations</li>
                  <li>You may not transfer, sell, or exchange Rewards except as expressly permitted by Pixano</li>
                </ul>
                <p>
                  Pixano makes no representations or warranties regarding the value, availability, or continued existence of Rewards.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. Intellectual Property</h2>
                <p className="mb-3">
                  The Services, including but not limited to the website, mobile application, software, technology, trademarks, logos, and
                  other content (excluding User Content), are owned by Pixano or its licensors and are protected by intellectual property laws.
                </p>
                <p>
                  These Terms do not grant you any right, title, or interest in the Services or Pixano&apos;s intellectual property, except
                  for the limited right to use the Services in accordance with these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. Third-Party Services</h2>
                <p>
                  The Services may contain links to third-party websites, services, or applications. Pixano does not endorse or assume
                  responsibility for third-party content, services, or privacy practices. Your interactions with third parties are solely
                  between you and the third party.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Disclaimers</h2>
                <p className="mb-3">
                  THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                  INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ACCURACY.
                </p>
                <p>
                  Pixano does not warrant that the Services will be uninterrupted, secure, error-free, or free from viruses or other harmful
                  components. Pixano does not guarantee the accuracy, completeness, or usefulness of any information on the Services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. Limitation of Liability</h2>
                <p className="mb-3">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, Pixano AI LLC AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND LICENSORS SHALL
                  NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA,
                  OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE),
                  OR ANY OTHER LEGAL THEORY, EVEN IF Pixano AI LLC HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
                <p className="mb-3">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, Pixano AI LLC&apos;S TOTAL LIABILITY FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THE SERVICES
                  SHALL NOT EXCEED THE AMOUNT YOU PAID TO Pixano AI LLC IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100),
                  WHICHEVER IS GREATER.
                </p>
                <p>
                  Some jurisdictions do not allow the exclusion of certain warranties or the limitation of liability for consequential or incidental
                  damages, so some of the above limitations may not apply to you. In such jurisdictions, Pixano AI LLC&apos;s liability is limited to the
                  maximum extent permitted by law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. Indemnification</h2>
                <p>
                  You agree to indemnify, defend, and hold harmless Pixano AI LLC and its affiliates, officers, directors, employees, agents, and licensors
                  from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys&apos; fees) arising
                  out of or related to: (a) your use of the Services, (b) your User Content, (c) your violation of these Terms, or (d) your
                  violation of any rights of another party.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">13. Dispute Resolution</h2>
                <p className="mb-3">
                  These Terms are governed by the laws of the United States and the state in which Pixano AI LLC is incorporated, without regard to
                  conflict of law principles.
                </p>
                <p>
                  Any disputes arising out of or related to these Terms or the Services shall be resolved through binding arbitration in
                  accordance with the rules of a recognized arbitration organization, except where prohibited by law. You and Pixano AI LLC waive any
                  right to a jury trial and to participate in class actions.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">14. Changes to These Terms</h2>
                <p className="mb-3">
                  Pixano may modify these Terms at any time by posting the updated Terms on the Services and updating the &quot;Last updated&quot; date.
                  Material changes will be communicated to you through the Services or other reasonable means.
                </p>
                <p>
                  Your continued use of the Services after changes become effective constitutes your acceptance of the updated Terms. If you do
                  not agree to the updated Terms, you must stop using the Services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">15. Contact</h2>
                <p>
                  If you have questions about these Terms of Service, please contact us at:
                </p>
                <p className="mt-2">
                  <strong>Email:</strong> support@pixano.ai
                </p>
              </section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;

